import { useEffect } from "react";

/*
 * Seo — lightweight, dependency-free SEO manager.
 * Mutates document.title, meta tags, canonical & JSON-LD on mount and cleans up on unmount.
 * Every page passes its unique metadata via props.
 */

const SITE = {
  name: "LIFTILE",
  domain: "https://www.liftile.com",
  brand: "LIFTILE — Rise With Confidence",
  defaultImage: "/liftile-og.png",
  twitter: "@liftile",
};

const SEO_TAG_ATTR = "data-liftile-seo";

function upsertMeta({ name, property, content }) {
  if (!content) return null;
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    el.setAttribute(SEO_TAG_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return { el, created };
}

function upsertLink({ rel, href }) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(SEO_TAG_ATTR, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return { el, created };
}

function upsertScript(id, jsonLd) {
  let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    el.setAttribute(SEO_TAG_ATTR, "true");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(jsonLd);
  return { el, created };
}

export default function Seo({
  title,
  description,
  path = "",
  image,
  keywords,
  type = "website",
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  jsonLd = null,
  jsonLdId = "page-schema",
}) {
  useEffect(() => {
    const alreadyBranded = title && /\bLIFTILE\b/i.test(title);
    const fullTitle = title
      ? alreadyBranded
        ? title
        : `${title} | ${SITE.name}`
      : SITE.brand;
    const canonical = `${SITE.domain}${path || (typeof window !== "undefined" ? window.location.pathname : "")}`;
    const ogImage = image
      ? image.startsWith("http")
        ? image
        : `${SITE.domain}${image}`
      : `${SITE.domain}${SITE.defaultImage}`;

    // Title
    const prevTitle = document.title;
    document.title = fullTitle;

    // Track created nodes to clean up on unmount
    const created = [];
    const track = (r) => {
      if (r && r.created) created.push(r.el);
    };

    track(upsertMeta({ name: "description", content: description }));
    track(upsertMeta({ name: "keywords", content: keywords }));
    track(upsertMeta({ name: "robots", content: robots }));
    track(upsertLink({ rel: "canonical", href: canonical }));

    // Open Graph
    track(upsertMeta({ property: "og:type", content: type }));
    track(upsertMeta({ property: "og:site_name", content: SITE.name }));
    track(upsertMeta({ property: "og:title", content: fullTitle }));
    track(upsertMeta({ property: "og:description", content: description }));
    track(upsertMeta({ property: "og:url", content: canonical }));
    track(upsertMeta({ property: "og:image", content: ogImage }));
    track(upsertMeta({ property: "og:locale", content: "en_IN" }));

    // Twitter
    track(upsertMeta({ name: "twitter:card", content: "summary_large_image" }));
    track(upsertMeta({ name: "twitter:site", content: SITE.twitter }));
    track(upsertMeta({ name: "twitter:title", content: fullTitle }));
    track(upsertMeta({ name: "twitter:description", content: description }));
    track(upsertMeta({ name: "twitter:image", content: ogImage }));

    // JSON-LD
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((graph, i) =>
        track(upsertScript(`${jsonLdId}-${i}`, graph))
      );
    }

    return () => {
      document.title = prevTitle;
      // Remove only nodes we created; leave pre-existing head elements untouched.
      created.forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [title, description, path, image, keywords, type, robots, jsonLd, jsonLdId]);

  return null;
}

/* Shared JSON-LD builders — used by each page. */
export const buildBreadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE.domain}${it.path}`,
  })),
});

export const buildProductSchema = ({
  name,
  description,
  image,
  sku,
  category,
  brand = "LIFTILE",
  specs = [],
  faqs = [],
}) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  image: (Array.isArray(image) ? image : [image])
    .filter(Boolean)
    .map((src) => (src.startsWith("http") ? src : `${SITE.domain}${src}`)),
  sku: sku || name,
  category,
  brand: {
    "@type": "Brand",
    name: brand,
  },
  manufacturer: {
    "@type": "Organization",
    name: "LIFTILE Machinery Pvt. Ltd.",
    url: SITE.domain,
  },
  additionalProperty: specs.map((s) => ({
    "@type": "PropertyValue",
    name: s.label,
    value: s.value,
  })),
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    priceCurrency: "INR",
    price: "0",
    priceSpecification: {
      "@type": "PriceSpecification",
      priceCurrency: "INR",
      description: "Contact LIFTILE for a customized quote.",
    },
    seller: {
      "@type": "Organization",
      name: "LIFTILE Machinery Pvt. Ltd.",
    },
    url: `${SITE.domain}/contact`,
  },
  ...(faqs.length
    ? {
        subjectOf: {
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      }
    : {}),
});

export const buildFaqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const buildArticleSchema = ({
  title,
  description,
  image,
  path,
  author = "LIFTILE Editorial Team",
  publishedAt,
  updatedAt,
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  image: image
    ? [image.startsWith("http") ? image : `${SITE.domain}${image}`]
    : undefined,
  author: { "@type": "Organization", name: author },
  publisher: {
    "@type": "Organization",
    name: "LIFTILE Machinery Pvt. Ltd.",
    logo: {
      "@type": "ImageObject",
      url: `${SITE.domain}/logo512.png`,
    },
  },
  datePublished: publishedAt,
  dateModified: updatedAt || publishedAt,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE.domain}${path}`,
  },
});

export { SITE };
